'use client'

import { authFetch } from '@/lib/auth/auth-fetch'
import { useWallet } from '@/providers/WalletContext'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Address, Hex } from 'viem'

type InitResponse = {
	typedData: {
		domain: { name: string; version: string; chainId: number; verifyingContract: Address }
		types: Record<string, Array<{ name: string; type: string }>>
		primaryType: string
		message: Record<string, unknown>
	}
	nonce: number
	amount: string
	toPerp: boolean
}

/**
 * Moves USDC between perp and spot wallets via `usdClassTransfer`.
 * Defaults to perp → spot (toPerp=false) — outcome markets need spot USDC.
 *
 * This is a USER-class action (not L1), so it CANNOT be signed by the
 * agent wallet. The user must sign each transfer with their embedded-wallet
 * EOA.
 */
export function useHyperliquidPerpToSpot() {
	const qc = useQueryClient()
	const { evmAccount } = useWallet()

	return useMutation({
		mutationFn: async (input: { amount: string; toPerp?: boolean }) => {
			if (!evmAccount) throw new Error('Wallet not ready')
			const toPerp = Boolean(input.toPerp)

			// 1) Server builds typed data + nonce
			const initRes = await authFetch('/api/hyperliquid/transfer/init', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ amount: input.amount, toPerp }),
			})
			if (!initRes.ok) {
				const e = (await initRes.json().catch(() => ({}))) as { error?: string }
				throw new Error(e.error || `init ${initRes.status}`)
			}
			const init = (await initRes.json()) as InitResponse

			// 2) User signs EIP-712 with the embedded-wallet EOA — same
			//    pattern as approveAgent / withdraw
			const sig = (await evmAccount.signTypedData({
				// biome-ignore lint/suspicious/noExplicitAny: typed-data shape comes from server
				domain: init.typedData.domain as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				types: init.typedData.types as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				primaryType: init.typedData.primaryType as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				message: init.typedData.message as any,
			})) as Hex

			// 3) Server posts signed action to HL /exchange
			const confirmRes = await authFetch('/api/hyperliquid/transfer/confirm', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					amount: init.amount,
					toPerp: init.toPerp,
					nonce: init.nonce,
					signature: sig,
				}),
			})
			if (!confirmRes.ok) {
				const e = (await confirmRes.json().catch(() => ({}))) as { error?: string }
				throw new Error(e.error || `confirm ${confirmRes.status}`)
			}
			return confirmRes.json()
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['hl-balance'] })
		},
	})
}
